import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster, toast } from "react-hot-toast";
import { AlertTriangle, BarChart2, LogOut, Lock, Zap, Check, Clock, MessageSquare, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useIsMobile } from "../hooks/useIsMobile";
import Navbar from "../components/Navbar";
import VehiclePanel from "../components/Sidebar";
import MapView from "../components/MapView";
import AddVehicleModal from "../components/AddVehicleModal";
import AICopilot from "../components/AICopilot";
import PaymentModal from "../components/PaymentModal";
import { useVehicleTracking } from "../hooks/useVehicleTracking";
import { useSimulatedPositions } from "../hooks/useSimulatedPositions";
import InstallPrompt from "../components/InstallPrompt";
import ShareModal from "../components/ShareModal";
import ActivityModal from "../components/ActivityModal";
import FeedbackModal from "../components/FeedbackModal";
import AccountModal from "../components/AccountModal";
import GstDetailsModal from "../components/GstDetailsModal";
import FleetTabs from "../components/FleetTabs";
import DriverPanel from "../components/DriverPanel";
import TripsPanel from "../components/TripsPanel";
import DocumentsPanel from "../components/DocumentsPanel";
import MaintenancePanel from "../components/MaintenancePanel";
import FuelLogPanel from "../components/FuelLogPanel";
import ExpensesPanel from "../components/ExpensesPanel";
import RequestsPanel from "../components/RequestsPanel";
import VehicleProfile from "../components/VehicleProfile";
import NotificationBell from "../components/NotificationBell";
import FleetDashboard from "../components/FleetDashboard";
import { useDriverRequests } from "../hooks/useDriverRequests";

/* ── API Usage bar ──────────────────────────────────────── */
function ApiUsageBar({ used, total, onTopUp }) {
  const pct   = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const left  = Math.max(0, total - used);
  const color = pct >= 90 ? "#DC2626" : pct >= 70 ? "#D97706" : "#16A34A";

  return (
    <div style={{
      display:"flex", alignItems:"center", gap:12,
      background:"#F9FAFB", border:"1px solid #E5E7EB",
      borderRadius:8, padding:"8px 14px",
    }}>
      <BarChart2 size={14} color={color} style={{ flexShrink:0 }} />
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
          <span style={{ fontSize:11, fontWeight:600, color:"#374151" }}>API Calls</span>
          <span style={{ fontSize:11, fontWeight:700, color }}>
            {left.toLocaleString("en-IN")} left
          </span>
        </div>
        <div style={{ height:4, background:"#E5E7EB", borderRadius:99, overflow:"hidden" }}>
          <div style={{ height:"100%", width:`${pct}%`, background:color, borderRadius:99, transition:"width 0.4s" }} />
        </div>
      </div>
      <button
        onClick={onTopUp}
        style={{
          background:"none", border:"1px solid #E5E7EB", borderRadius:6,
          padding:"4px 10px", fontSize:11, fontWeight:600, color:"#4F46E5",
          cursor:"pointer", whiteSpace:"nowrap", transition:"all 0.15s",
        }}
        onMouseOver={e => { e.currentTarget.style.background="#EFF6FF"; e.currentTarget.style.borderColor="#BFDBFE"; }}
        onMouseOut={e  => { e.currentTarget.style.background="none";    e.currentTarget.style.borderColor="#E5E7EB"; }}
      >
        Top Up
      </button>
    </div>
  );
}

/* ── Exhausted wall ─────────────────────────────────────── */
function ExhaustedWall({ onAction, canTopup }) {
  return (
    <div style={{
      flex:1, display:"flex", alignItems:"center", justifyContent:"center",
      background:"#F7F8FA",
    }}>
      <div style={{
        width:"100%", maxWidth:460, textAlign:"center",
        background:"#ffffff", border:"1px solid #E5E7EB",
        borderRadius:16, padding:"40px",
        boxShadow:"0 4px 24px rgba(0,0,0,0.06)",
      }}>
        <div style={{
          width:56, height:56, borderRadius:14,
          background:"#FEF2F2", border:"1px solid #FECACA",
          display:"flex", alignItems:"center", justifyContent:"center",
          margin:"0 auto 20px",
        }}>
          <AlertTriangle size={24} color="#DC2626" />
        </div>
        <div style={{ fontSize:20, fontWeight:700, color:"#111827", marginBottom:8 }}>
          {canTopup ? "API Quota Exhausted" : "Free Trial Ended"}
        </div>
        <div style={{ fontSize:14, color:"#6B7280", lineHeight:1.6, marginBottom:20 }}>
          {canTopup
            ? "Your quota is used up. Top up at ₹3.75/call + GST to continue tracking."
            : "Your 2 free trial calls have been used. Purchase a plan to continue tracking — no free calls remain."}
        </div>
        <div style={{
          background:"#FEF2F2", border:"1px solid #FECACA",
          borderRadius:10, padding:"12px 16px", marginBottom:24,
          fontSize:13, color:"#991B1B",
        }}>
          Tracking is paused until payment is completed. Your vehicle data is preserved.
        </div>
        <button
          onClick={onAction}
          style={{
            width:"100%", height:46, borderRadius:10,
            background: canTopup ? "#7C3AED" : "#4F46E5",
            border:"none", color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer",
            boxShadow: canTopup ? "0 2px 8px rgba(124,58,237,0.3)" : "0 2px 8px rgba(79,70,229,0.3)",
          }}
          onMouseOver={e => e.currentTarget.style.background = canTopup ? "#6D28D9" : "#4338CA"}
          onMouseOut={e  => e.currentTarget.style.background = canTopup ? "#7C3AED" : "#4F46E5"}
        >
          {canTopup ? "Top Up API Calls — ₹3.75/call + GST" : "Choose a Plan →"}
        </button>
      </div>
    </div>
  );
}

/* ── Paywall overlay — shown on map when user hasn't paid ── */
function PaywallOverlay({ onPay, hasVehicles }) {
  return (
    <motion.div
      initial={{ opacity:0 }}
      animate={{ opacity:1 }}
      style={{
        position:"absolute", inset:0, zIndex:20,
        display:"flex", alignItems:"center", justifyContent:"center",
        background:"rgba(15,23,42,0.5)",
      }}
    >
      <motion.div
        initial={{ scale:.94, y:20, opacity:0 }}
        animate={{ scale:1,   y:0,  opacity:1 }}
        transition={{ duration:0.22, ease:"easeOut" }}
        style={{
          width:"100%", maxWidth:390, margin:16,
          background:"#ffffff", borderRadius:18,
          overflow:"hidden",
          boxShadow:"0 32px 80px rgba(0,0,0,0.24)",
          border:"1px solid #EBEBEB",
        }}
      >
        <div style={{ height:4, background:"#4F46E5" }} />
        <div style={{ padding:"28px 26px 24px", textAlign:"center" }}>
          <div style={{
            width:52, height:52, borderRadius:13,
            background:"#EEF2FF", border:"1.5px solid #C7D2FE",
            display:"flex", alignItems:"center", justifyContent:"center",
            margin:"0 auto 16px",
          }}>
            <Lock size={21} color="#4F46E5" />
          </div>

          <div style={{ fontSize:19, fontWeight:800, color:"#111827", marginBottom:6 }}>
            {hasVehicles ? "One Step Away" : "Activate Live Tracking"}
          </div>

          {/* Dynamic guidance based on whether vehicle was added */}
          {hasVehicles ? (
            <div style={{
              display:"inline-flex", alignItems:"center", gap:6,
              background:"#F0FDF4", border:"1px solid #BBF7D0",
              borderRadius:99, padding:"5px 14px", marginBottom:14,
            }}>
              <div style={{ width:7, height:7, borderRadius:"50%", background:"#16A34A", flexShrink:0 }} />
              <span style={{ fontSize:12, fontWeight:600, color:"#15803D" }}>
                Vehicle added — complete payment to track
              </span>
            </div>
          ) : (
            <div style={{
              fontSize:13, color:"#6B7280", lineHeight:1.65, marginBottom:14,
            }}>
              Add your vehicle plate number from the sidebar, then pay once to unlock FASTag tracking.
            </div>
          )}

          {/* Plan grid */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8, marginBottom:16 }}>
            {[
              { calls:"50",  price:"₹300", gst:"₹354 incl. GST" },
              { calls:"100", price:"₹550", gst:"₹649 incl. GST" },
              { calls:"150", price:"₹750", gst:"₹885 incl. GST" },
              { calls:"200", price:"₹900", gst:"₹1,062 incl. GST", best:true },
            ].map(p => (
              <div key={p.calls} style={{
                background: p.best ? "#EEF2FF" : "#F8FAFC",
                border:`1.5px solid ${p.best ? "#C7D2FE" : "#E5E7EB"}`,
                borderRadius:10, padding:"10px 12px", textAlign:"left",
              }}>
                <div style={{ fontSize:13, fontWeight:800, color: p.best ? "#4F46E5" : "#111827" }}>
                  {p.calls} calls
                  {p.best && <span style={{ fontSize:9, fontWeight:700, background:"#6366F1", color:"#fff", borderRadius:4, padding:"1px 5px", marginLeft:6 }}>BEST</span>}
                </div>
                <div style={{ fontSize:14, fontWeight:800, color:"#111827", marginTop:4 }}>{p.price}</div>
                <div style={{ fontSize:10, color:"#9CA3AF" }}>{p.gst}</div>
              </div>
            ))}
          </div>

          <button
            onClick={onPay}
            style={{
              width:"100%", height:46, borderRadius:10,
              background:"#4F46E5", border:"none",
              color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer",
              boxShadow:"0 3px 12px rgba(79,70,229,0.32)",
              transition:"background 0.15s",
            }}
            onMouseOver={e => e.currentTarget.style.background="#4338CA"}
            onMouseOut={e  => e.currentTarget.style.background="#4F46E5"}
          >
            Choose a Plan →
          </button>
          <div style={{ fontSize:11, color:"#9CA3AF", marginTop:10 }}>
            Secured payment · 256-bit SSL
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── Main org app ───────────────────────────────────────── */
export default function OrgApp() {
  const { session, currentOrg, isPaid, isExhausted, isSuspended, consumeApiCall, saveOrgVehicles, logout, canTopup, isFleet } = useAuth();
  const navigate  = useNavigate();
  const isMobile  = useIsMobile();
  const { requests: pendingDriverRequests } = useDriverRequests("pending", undefined, isFleet);

  const [showPayment,      setShowPayment]      = useState(false);
  const [paymentMode,      setPaymentMode]      = useState(null); // overrides the default topup/plans mode when set (e.g. "fleet_upgrade")
  const [profileVehicle,   setProfileVehicle]   = useState(null); // vehicle object, or null — drives the Vehicle Profile overlay
  const [showAddModal,     setShowAddModal]     = useState(false);
  const [pendingTrackId,   setPendingTrackId]   = useState(null);
  const [pendingAddVehicle, setPendingAddVehicle] = useState(false);
  const [focusedToll,    setFocusedToll]    = useState(null);
  const [showCopilot,    setShowCopilot]    = useState(false);
  const [searchQuery,    setSearchQuery]    = useState("");
  const [drawerOpen,     setDrawerOpen]     = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [shareVehicleId, setShareVehicleId] = useState(null);
  const [showActivity,   setShowActivity]   = useState(false);
  const [showFeedback,   setShowFeedback]   = useState(false);
  const [showAccount,    setShowAccount]    = useState(false);
  const [activeTab,      setActiveTab]      = useState(() => (isFleet ? "dashboard" : "tracking"));
  const [gstModalDismissed, setGstModalDismissed] = useState(false);

  // Billing is owner-only — dispatchers can see usage but not manage the plan
  const isDispatcher = currentOrg?.role === "dispatcher";
  // Prompt once per session for orgs that paid before GST/PAN was collected at
  // signup — dismissible for now, resurfaces on next login until filled in.
  const showGstPrompt = !isDispatcher && canTopup && !currentOrg?.gstNumber
    && !currentOrg?.panNumber && !gstModalDismissed;
  function openPayment() {
    if (isDispatcher) {
      toast.error("Only the account owner can manage billing.");
      return;
    }
    setPaymentMode(null);
    setShowPayment(true);
  }

  function openFleetUpgrade() {
    if (isDispatcher) {
      toast.error("Only the account owner can manage billing.");
      return;
    }
    setPaymentMode("fleet_upgrade");
    setShowPayment(true);
  }


  useEffect(() => {
    if (!session) navigate("/auth", { replace:true });
  }, [session, navigate]);

  const handleApiCall = useCallback(() => {
    if (!isPaid || isExhausted) return false;
    return consumeApiCall();
  }, [isPaid, isExhausted, consumeApiCall]);

  const {
    vehicles, selectedVehicle, loading, zoomTrigger,
    addVehicle, removeVehicle, selectVehicle, setSelectedVehicle,
    startTracking, stopTracking, refreshVehicle, setManualLocation, pushCache,
  } = useVehicleTracking({
    initialVehicles: currentOrg?.vehicles || [],
    onApiCall: isPaid ? handleApiCall : null,
    onVehiclesChange: saveOrgVehicles,
  });

  function openShareModal(vehicleId) {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (vehicle) pushCache(vehicleId, vehicle); // ensure cache is populated before share token is created
    setShareVehicleId(vehicleId);
  }

  // Simulated positions update every 30 s for vehicles between toll crossings
  const estimatedPositions = useSimulatedPositions(vehicles);

  function handleTollClick(toll) {
    setFocusedToll(prev => prev?.id === toll.id ? null : toll);
  }

  function handleLogout() { logout(); navigate("/"); }

  // Track button clicked on a vehicle card
  function handleTrackVehicle(vehicleId) {
    if (!isPaid) {
      setPendingTrackId(vehicleId);
      openPayment();
    } else {
      startTracking(vehicleId);
      const vehicle = vehicles.find(v => v.id === vehicleId);
      if (vehicle) setSelectedVehicle({ ...vehicle, isTracking: true, status: "Active" });
    }
  }

  function handleStopTrackVehicle(vehicleId) {
    stopTracking(vehicleId);
  }

  // Gate: show payment first if not paid, open modal if paid
  function handleAddVehicleClick() {
    if (!isPaid) {
      setPendingAddVehicle(true);
      openPayment();
    } else {
      setShowAddModal(true);
    }
  }

  async function handleAddVehicle(formData) {
    return await addVehicle(formData);
  }

  // Auto-expand drawer when a vehicle is selected on mobile
  useEffect(() => {
    if (isMobile && selectedVehicle) setDrawerOpen(true);
  }, [isMobile, selectedVehicle]);

  // Only tracked vehicles appear on the map
  const mapVehicles = vehicles.filter(v => v.isTracking);

  if (!session) return null;

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100vh", overflow:"hidden", background:"#F7F8FA" }}>
      <Toaster
        position="top-center"
        toastOptions={{
          style:{
            background:"#fff", color:"#111827",
            border:"1px solid #E5E7EB", borderRadius:10,
            boxShadow:"0 4px 16px rgba(0,0,0,0.08)", fontSize:13,
          },
          success:{ iconTheme:{ primary:"#16A34A", secondary:"#fff" } },
          error:  { iconTheme:{ primary:"#DC2626", secondary:"#fff" } },
        }}
      />

      {/* ── Navbar ── */}
      <div style={{ flexShrink:0 }}>
        <Navbar
          vehicles={vehicles}
          onSearch={setSearchQuery}
          searchQuery={searchQuery}
          extraRight={
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              {isFleet ? (
                <NotificationBell
                  onSelect={n => setActiveTab(n.type === "maintenance_due" ? "maintenance" : "documents")}
                />
              ) : !isDispatcher && (
                <button
                  onClick={openFleetUpgrade}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    background: "#059669", border: "none", borderRadius: 8,
                    padding: isMobile ? "0 10px" : "0 14px", height: 36, cursor: "pointer",
                    fontSize: 13, fontWeight: 700, color: "#fff",
                  }}
                >
                  {isMobile ? "Upgrade" : "Upgrade to Fleet Management →"}
                </button>
              )}
              {isPaid && !isMobile && (
                <div style={{ width:220 }}>
                  <ApiUsageBar
                    used={currentOrg.apiCallsUsed}
                    total={currentOrg.apiCallsTotal}
                    onTopUp={openPayment}
                  />
                </div>
              )}
              {isPaid && (
                <button
                  onClick={() => setShowActivity(true)}
                  title="API Activity Log"
                  style={{
                    display:"flex", alignItems:"center", gap:6,
                    background:"none", border:"1px solid #E5E7EB",
                    borderRadius:8, padding: isMobile ? "0 10px" : "0 12px", height:36,
                    fontSize:13, fontWeight:500, color:"#6B7280", cursor:"pointer",
                    transition:"all 0.15s",
                  }}
                  onMouseOver={e => { e.currentTarget.style.background="#F3F4F6"; e.currentTarget.style.color="#111827"; }}
                  onMouseOut={e  => { e.currentTarget.style.background="none";    e.currentTarget.style.color="#6B7280"; }}
                >
                  <Clock size={13} />
                  {!isMobile && " Activity"}
                </button>
              )}
              <button
                onClick={() => setShowAccount(true)}
                title="Account"
                style={{
                  display:"flex", alignItems:"center", gap:6,
                  background:"none", border:"1px solid #E5E7EB",
                  borderRadius:8, padding: isMobile ? "0 10px" : "0 12px", height:36,
                  fontSize:13, fontWeight:500, color:"#6B7280", cursor:"pointer",
                  transition:"all 0.15s",
                }}
                onMouseOver={e => { e.currentTarget.style.background="#F3F4F6"; e.currentTarget.style.color="#111827"; }}
                onMouseOut={e  => { e.currentTarget.style.background="none";    e.currentTarget.style.color="#6B7280"; }}
              >
                <User size={13} />
                {!isMobile && " Account"}
              </button>
              <button
                onClick={handleLogout}
                style={{
                  display:"flex", alignItems:"center", gap:6,
                  background:"none", border:"1px solid #E5E7EB",
                  borderRadius:8, padding: isMobile ? "0 10px" : "0 12px", height:36,
                  fontSize:13, fontWeight:500, color:"#6B7280", cursor:"pointer",
                  transition:"all 0.15s",
                }}
                onMouseOver={e => { e.currentTarget.style.background="#F3F4F6"; e.currentTarget.style.color="#111827"; }}
                onMouseOut={e  => { e.currentTarget.style.background="none";    e.currentTarget.style.color="#6B7280"; }}
              >
                <LogOut size={13} />
                {!isMobile && " Sign Out"}
              </button>
            </div>
          }
        />
      </div>

      <FleetTabs activeTab={activeTab} onChangeTab={setActiveTab} isFleet={isFleet} pendingRequestsCount={isFleet ? pendingDriverRequests.length : 0} />

      {/* ── Info ticker ── */}
      <div style={{
        flexShrink:0, background:"#0F172A", overflow:"hidden",
        height:26, display:"flex", alignItems:"center",
      }}>
        <div style={{
          display:"inline-flex",
          animation:"ticker 40s linear infinite",
          whiteSpace:"nowrap",
        }}>
          {[0,1].map(i => (
            <span key={i} style={{ fontSize:11.5, fontWeight:500, color:"#94A3B8", letterSpacing:0.3, paddingRight:120 }}>
              Toll crossing data displayed on this platform is sourced from the National FASTag system and reflects transactions recorded within the last 72 hours only — crossings older than 72 hours are not retained or displayed.
            </span>
          ))}
        </div>
      </div>

      {/* ── Body ── */}
      {isSuspended ? (
        <div style={{
          flex:1, display:"flex", alignItems:"center", justifyContent:"center",
          background:"#FFF7ED", padding:32,
        }}>
          <div style={{
            maxWidth:460, textAlign:"center",
            background:"#fff", border:"1.5px solid #FED7AA",
            borderRadius:16, padding:"40px 32px",
            boxShadow:"0 4px 24px rgba(0,0,0,0.07)",
          }}>
            <div style={{
              width:56, height:56, borderRadius:14, background:"#FEF3C7",
              display:"flex", alignItems:"center", justifyContent:"center",
              margin:"0 auto 16px",
            }}>
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div style={{ fontSize:18, fontWeight:700, color:"#111827", marginBottom:8 }}>
              Account Suspended
            </div>
            <div style={{ fontSize:14, color:"#6B7280", lineHeight:1.6, marginBottom:24 }}>
              Your access has been suspended by the administrator. This usually means a payment is required to continue using the service.
            </div>
            <div style={{
              background:"#FFF7ED", border:"1px solid #FED7AA",
              borderRadius:10, padding:"12px 16px", marginBottom:20,
              fontSize:13, color:"#92400E",
            }}>
              Contact <strong>support</strong> or reply to your onboarding email to complete payment and restore access.
            </div>
            <button
              onClick={logout}
              style={{
                background:"#111827", color:"#fff", border:"none",
                borderRadius:8, padding:"10px 24px", fontSize:14,
                fontWeight:600, cursor:"pointer",
              }}
            >Sign Out</button>
          </div>
        </div>
      ) : isExhausted ? (
        <ExhaustedWall onAction={openPayment} canTopup={canTopup} />
      ) : activeTab === "dashboard" ? (
        <FleetDashboard onNavigate={setActiveTab} vehicles={vehicles} onOpenVehicleProfile={setProfileVehicle} />
      ) : activeTab === "drivers" ? (
        <DriverPanel vehicles={vehicles} />
      ) : activeTab === "trips" ? (
        <TripsPanel vehicles={vehicles} />
      ) : activeTab === "documents" ? (
        <DocumentsPanel vehicles={vehicles} />
      ) : activeTab === "maintenance" ? (
        <MaintenancePanel vehicles={vehicles} />
      ) : activeTab === "fuel" ? (
        <FuelLogPanel vehicles={vehicles} />
      ) : activeTab === "expenses" ? (
        <ExpensesPanel vehicles={vehicles} />
      ) : activeTab === "requests" ? (
        <RequestsPanel />
      ) : (
        <main style={{ flex:1, position:"relative", overflow:"hidden" }}>
          {/* Map — shrinks to the right of the panel when it's open */}
          <div style={{
            position:"absolute",
            top:0, right:0, bottom:0,
            left: (!isMobile && !panelCollapsed) ? 412 : 0,
            transition:"left 0.3s cubic-bezier(0.32,0.72,0,1), filter 0.35s, transform 0.35s",
            filter: !isPaid ? "blur(6px) brightness(0.88) saturate(0.8)" : "none",
            transform: !isPaid ? "scale(1.04)" : "scale(1)",
            pointerEvents: !isPaid ? "none" : "auto",
          }}>
            <MapView
              vehicles={mapVehicles}
              selectedVehicle={selectedVehicle}
              onSelectVehicle={v => { selectVehicle(v); setFocusedToll(null); }}
              focusedToll={focusedToll}
              onTollClick={handleTollClick}
              estimatedPositions={estimatedPositions}
              zoomTrigger={zoomTrigger}
            />
          </div>

          {/* Paywall overlay — covers only the map area */}
          {!isPaid && (
            <div style={{
              position:"absolute",
              top:0, right:0, bottom:0,
              left: (!isMobile && !panelCollapsed) ? 412 : 0,
              transition:"left 0.3s cubic-bezier(0.32,0.72,0,1)",
              zIndex:20,
            }}>
              <PaywallOverlay onPay={openPayment} hasVehicles={vehicles.length > 0} />
            </div>
          )}

          {/* ── Desktop: floating left panel + collapse toggle ── */}
          {!isMobile && (
            <>
              {/* Panel — slides off-screen when collapsed */}
              <div style={{
                position:"absolute", left:16, top:16, bottom:16, width:380,
                zIndex:10, borderRadius:16, overflow:"hidden",
                background:"#ffffff",
                border:"1px solid rgba(0,0,0,0.08)",
                boxShadow:"0 20px 60px rgba(0,0,0,0.28), 0 4px 16px rgba(0,0,0,0.12)",
                transform: panelCollapsed ? "translateX(calc(-100% - 24px))" : "translateX(0)",
                transition:"transform 0.3s cubic-bezier(0.32,0.72,0,1)",
              }}>
                <VehiclePanel
                  vehicles={vehicles}
                  selectedVehicle={selectedVehicle}
                  onSelectVehicle={v => v ? selectVehicle(v) : setSelectedVehicle(null)}
                  onRemoveVehicle={removeVehicle}
                  onAddVehicle={handleAddVehicleClick}
                  onTrackVehicle={handleTrackVehicle}
                  onStopTrackVehicle={handleStopTrackVehicle}
                  onRefreshVehicle={refreshVehicle}
                  onForceRefreshVehicle={id => refreshVehicle(id, true)}
                  onSetManualLocationVehicle={setManualLocation}
                  onTollClick={handleTollClick}
                  activeTollId={focusedToll?.id}
                  searchQuery={searchQuery}
                  estimatedPositions={estimatedPositions}
                  onShareVehicle={openShareModal}
                  onViewProfile={isFleet ? setProfileVehicle : undefined}
                />
              </div>

              {/* Collapse / expand toggle tab */}
              <button
                onClick={() => setPanelCollapsed(v => !v)}
                title={panelCollapsed ? "Show fleet panel" : "Hide fleet panel"}
                style={{
                  position:"absolute", zIndex:11,
                  left: panelCollapsed ? 16 : 396,
                  top:"50%", transform:"translateY(-50%)",
                  transition:"left 0.3s cubic-bezier(0.32,0.72,0,1)",
                  width:28, height:56,
                  background:"#ffffff",
                  border:"1px solid rgba(0,0,0,0.1)",
                  borderRadius:"0 10px 10px 0",
                  boxShadow:"4px 0 16px rgba(0,0,0,0.12)",
                  cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  padding:0,
                }}
                onMouseOver={e => e.currentTarget.style.background="#F5F5F5"}
                onMouseOut={e  => e.currentTarget.style.background="#ffffff"}
              >
                <svg
                  width="12" height="12" viewBox="0 0 12 12" fill="none"
                  style={{
                    transform: panelCollapsed ? "rotate(0deg)" : "rotate(180deg)",
                    transition:"transform 0.3s",
                  }}
                >
                  <path d="M4.5 2L8.5 6L4.5 10" stroke="#6B7280" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </>
          )}

          {/* ── Mobile: bottom drawer ── */}
          {isMobile && (
            <>
              {/* Tap-on-map closes drawer */}
              {drawerOpen && (
                <div
                  onClick={() => setDrawerOpen(false)}
                  style={{ position:"absolute", inset:0, zIndex:19, background:"rgba(0,0,0,0.12)" }}
                />
              )}

              <div className="safe-bottom" style={{
                position:"absolute", left:0, right:0, bottom:0,
                height: drawerOpen ? "82vh" : 112,
                zIndex:25, borderRadius:"20px 20px 0 0",
                background:"#ffffff",
                boxShadow:"0 -8px 32px rgba(0,0,0,0.18), 0 -1px 0 rgba(0,0,0,0.06)",
                transition:"height 0.32s cubic-bezier(0.32,0.72,0,1)",
                display:"flex", flexDirection:"column",
                overflow:"hidden",
              }}>
                {/* Drag handle */}
                <div
                  onClick={() => setDrawerOpen(v => !v)}
                  style={{
                    padding:"10px 0 6px", display:"flex", alignItems:"center", justifyContent:"center",
                    cursor:"pointer", flexShrink:0,
                  }}
                >
                  <div style={{ width:40, height:4, borderRadius:99, background:"#E2E8F0" }} />
                </div>

                {/* Mobile API usage strip — only when paid, always visible */}
                {isPaid && (
                  <div style={{
                    padding:"0 16px 8px", flexShrink:0,
                    display: drawerOpen ? "none" : "block",
                  }}>
                    <ApiUsageBar
                      used={currentOrg.apiCallsUsed}
                      total={currentOrg.apiCallsTotal}
                      onTopUp={openPayment}
                    />
                  </div>
                )}

                {/* VehiclePanel fills the drawer */}
                <div style={{ flex:1, overflow:"hidden" }}>
                  <VehiclePanel
                    vehicles={vehicles}
                    selectedVehicle={selectedVehicle}
                    onSelectVehicle={v => v ? selectVehicle(v) : setSelectedVehicle(null)}
                    onRemoveVehicle={removeVehicle}
                    onAddVehicle={handleAddVehicleClick}
                    onTrackVehicle={handleTrackVehicle}
                    onStopTrackVehicle={handleStopTrackVehicle}
                    onRefreshVehicle={refreshVehicle}
                    onForceRefreshVehicle={id => refreshVehicle(id, true)}
                    onTollClick={handleTollClick}
                    activeTollId={focusedToll?.id}
                    searchQuery={searchQuery}
                    estimatedPositions={estimatedPositions}
                    onShareVehicle={openShareModal}
                    onViewProfile={isFleet ? setProfileVehicle : undefined}
                  />
                </div>
              </div>
            </>
          )}
        </main>
      )}

      {/* ── Modals ── */}
      <AnimatePresence>
        {showAddModal && (
          <AddVehicleModal
            key="add-vehicle"
            onClose={() => setShowAddModal(false)}
            onSubmit={handleAddVehicle}
            loading={loading}
          />
        )}
        {profileVehicle && (
          <VehicleProfile
            key="vehicle-profile"
            vehicle={profileVehicle}
            onClose={() => setProfileVehicle(null)}
            onNavigate={setActiveTab}
          />
        )}
        {showPayment && (
          <PaymentModal
            key="payment"
            mode={paymentMode || (canTopup ? "topup" : "plans")}
            onClose={() => {
              setShowPayment(false);
              setPaymentMode(null);
              setPendingTrackId(null);
              setPendingAddVehicle(false);
            }}
            onSuccess={() => {
              setShowPayment(false);
              if (pendingAddVehicle) {
                setShowAddModal(true);
                setPendingAddVehicle(false);
              }
              if (pendingTrackId) {
                startTracking(pendingTrackId);
                const vehicle = vehicles.find(v => v.id === pendingTrackId);
                if (vehicle) setSelectedVehicle({ ...vehicle, isTracking: true, status: "Active" });
                setPendingTrackId(null);
              }
            }}
          />
        )}
        {shareVehicleId && (() => {
          const sv = vehicles.find(v => v.id === shareVehicleId);
          return sv ? (
            <ShareModal
              key="share"
              vehicle={sv}
              onClose={() => setShareVehicleId(null)}
            />
          ) : null;
        })()}
      </AnimatePresence>

      <AICopilot
        vehicles={vehicles}
        isOpen={showCopilot}
        onToggle={() => setShowCopilot(v => !v)}
      />

      <InstallPrompt />

      {showActivity && <ActivityModal onClose={() => setShowActivity(false)} />}

      {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} />}

      {showAccount && (
        <AccountModal
          org={currentOrg}
          onClose={() => setShowAccount(false)}
          onDeleted={() => { logout(); navigate("/"); }}
        />
      )}

      {showGstPrompt && (
        <GstDetailsModal onClose={() => setGstModalDismissed(true)} />
      )}

      {/* Floating feedback button — sits above the AICopilot button (bottom:24 + 46px height + 10px gap = 80) */}
      <button
        onClick={() => setShowFeedback(true)}
        title="Send feedback"
        style={{
          position: "fixed",
          bottom: 80,
          right: 24,
          zIndex: 1100,
          display: "flex", alignItems: "center", gap: 6,
          background: "#1E293B",
          border: "1px solid rgba(99,102,241,0.3)",
          borderRadius: 10,
          padding: "8px 13px",
          cursor: "pointer",
          boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
          color: "#818CF8",
          fontSize: 12,
          fontWeight: 600,
          transition: "all 0.18s",
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = "#6366F1";
          e.currentTarget.style.color = "#fff";
          e.currentTarget.style.borderColor = "#6366F1";
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = "#1E293B";
          e.currentTarget.style.color = "#818CF8";
          e.currentTarget.style.borderColor = "rgba(99,102,241,0.3)";
        }}
      >
        <MessageSquare size={13} />
        <span>Feedback</span>
      </button>

      {/* ── App footer ── */}
      <div style={{
        flexShrink:0,
        background:"#0F172A",
        borderTop:"1px solid rgba(255,255,255,0.05)",
        display:"flex", alignItems:"center", justifyContent:"center",
        flexWrap:"wrap", gap: isMobile ? 8 : 20,
        padding: isMobile ? "5px 16px" : "0 20px",
        minHeight:28,
        paddingBottom:"var(--sab, 0px)",
      }}>
        <span style={{ fontSize:11, color:"#475569", whiteSpace:"nowrap" }}>
          Made with <span style={{ color:"#F87171" }}>❤</span> in India by{" "}
          <a href="https://gvalabs.in/" target="_blank" rel="noopener noreferrer"
            style={{ color:"#6366F1", textDecoration:"none", fontWeight:600 }}>
            GVALabs
          </a>
        </span>
        {!isMobile && (
          <>
            <span style={{ color:"rgba(255,255,255,0.1)" }}>|</span>
            {[
              { href:"/terms",   label:"Terms"   },
              { href:"/privacy", label:"Privacy" },
              { href:"/refund",  label:"Refund"  },
            ].map(l => (
              <a key={l.href} href={l.href}
                style={{ fontSize:11, color:"#475569", textDecoration:"none", transition:"color 0.15s", whiteSpace:"nowrap" }}
                onMouseEnter={e => e.currentTarget.style.color="#818CF8"}
                onMouseLeave={e => e.currentTarget.style.color="#475569"}
              >{l.label}</a>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
